import React, { useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { readAxiosErr } from 'utils/misc';
import { Layout } from 'components/Form';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { scheduleAdjMtg, useAdjMtgConfig } from 'queries/job';
import { useRepChoices } from 'queries/company';
import { serverTimeToDate, startOfHour } from 'utils/time';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import SkeletonLoader, { ContentType } from 'components/SkeletonLoader';

interface IFormValues {
  rep: number;
  date: string;
  end_date?: string;
  comment: string;
  notify_customer?: boolean;
  add_to_user_cal?: boolean;
}

type AppProps = {
  jobId: string;
  closeModal: () => void;
};

const ScheduleAdjMtg: React.FC<AppProps> = ({ jobId, closeModal }) => {
  const queryClient = useQueryClient();

  const { data: config, isFetching } = useAdjMtgConfig(jobId);

  const scheduleAdjMtgMutation = useMutation(
    (formData: any) => scheduleAdjMtg(jobId, formData),
    {
      onError: (err: any) => {
        toast.error(readAxiosErr(err));
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['job-last-checklist', jobId]),
          queryClient.invalidateQueries(['job-checklist', jobId]),
        ]);
        queryClient.invalidateQueries(['job-detail', jobId]);
        queryClient.invalidateQueries(['jobs']);

        return closeModal();
      },
    }
  );

  const schema = yup.object(
    processSchema(
      {
        inspector: yup.number(),
        date: yup.date(),
        end_date: yup.date(),
        note: yup.string(),
        notify_customer: yup.boolean(),
        add_to_user_cal: yup.boolean(),
      },
      config
    )
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
    reset,
    setValue,
  } = useForm<IFormValues>({ resolver: yupResolver(schema) });

  const onSubmit: SubmitHandler<IFormValues> = async (formData) => {
    scheduleAdjMtgMutation.mutate(formData);
  };

  const _start = watch('date');
  const _end = watch('end_date');

  useEffect(() => {
    if (config) {
      const defaultValues = processInitialValues(config);
      if (!config.date?.initial) {
        defaultValues['date'] = startOfHour(1);
      } else {
        defaultValues['date'] = serverTimeToDate(config.date.initial);
      }

      if (!config.end_date?.initial) {
        defaultValues['end_date'] = startOfHour(2);
      } else {
        defaultValues['end_date'] = serverTimeToDate(config.end_date.initial);
      }
      reset(defaultValues);
    }
  }, []);

  useEffect(() => {
    if (_end && _start > _end) {
      setValue('end_date', _start);
    }
  }, [_start, _end]);

  const fields = [
    {
      name: 'rep',
      type: FieldType.AsyncSelect,
      label: 'Select Rep',
      error: errors.rep?.message,
      control,
      optionsPromise: useRepChoices,
    },
    {
      name: 'date',
      type: FieldType.DatePicker,
      label: 'Start At',
      placeholder: 'Start At',
      error: errors.date?.message,
      isClearable: true,
      control,
    },
    {
      name: 'end_date',
      type: FieldType.DatePicker,
      label: 'End At',
      placeholder: 'End At',
      error: errors.end_date?.message,
      isClearable: true,
      control,
    },
    {
      type: FieldType.TextArea,
      label: 'Comment',
      placeholder: 'Type here...',
      error: errors.comment?.message,
      ...register('comment'),
    },
    {
      type: FieldType.Checkbox,
      label: 'Notify Customer',
      error: errors.notify_customer?.message,
      className: 'my-4',
      ...register('notify_customer'),
    },
    {
      type: FieldType.Checkbox,
      label: 'Add to my calendar',
      error: errors.add_to_user_cal?.message,
      className: 'my-4',
      ...register('add_to_user_cal'),
    },
  ];

  if (isFetching) {
    return <SkeletonLoader contentType={ContentType.Form} size={8} />;
  }

  return (
    <div className="pb-4">
      <div className="flex justify-between mb-6">
        <div className="page-heading">Schedule Adjuster Meeting</div>
        {!scheduleAdjMtgMutation.isLoading && (
          <div className="relative bottom-1">
            <Icon
              name="close"
              size={36}
              onClick={closeModal}
              className="cursor-pointer"
            />
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Layout
          fields={processFields(fields, config)}
          loading={scheduleAdjMtgMutation.isLoading}
        />

        <Button
          label={
            scheduleAdjMtgMutation.isLoading
              ? 'Scheduling...'
              : 'Schedule Adjuster Meeting'
          }
          className="mt-8"
          loading={scheduleAdjMtgMutation.isLoading}
        />
      </form>
    </div>
  );
};

export default ScheduleAdjMtg;
